import type { OrgIntent, OrgMember, OrgTeam } from "@/types/api";
import { http } from "./http";

/**
 * Teams: the groups inside an organization, and what each of them is trying to
 * learn.
 *
 * NOTHING CONSUMES INTENT YET. The Gateway stores it and hands it back; no feed
 * or plan reads it (CUR-126). So the screens built on this service are for
 * capturing what a partner wants, not for showing an effect — and they should
 * not imply one.
 *
 * The org is never named in any request. It comes from the caller's own
 * membership on the server side, which is why no method here takes an org id.
 */
export const teamsService = {
	list: (
		opts: { includeMembers?: boolean; includeArchived?: boolean } = {},
		signal?: AbortSignal,
	) =>
		http.get<{ teams: OrgTeam[] }>("/api/org/teams", {
			query: {
				include: opts.includeMembers ? "members" : undefined,
				includeArchived: opts.includeArchived ? "true" : undefined,
			},
			signal,
		}),

	get: (teamId: string, signal?: AbortSignal) =>
		http.get<{ team: OrgTeam; members: OrgMember[] }>(
			`/api/org/teams/${teamId}`,
			{ signal },
		),

	create: (input: {
		slug: string;
		name: string;
		description?: string | null;
		intent?: OrgIntent;
	}) => http.post<{ team: OrgTeam }>("/api/org/teams", input),

	/**
	 * A partial update, except for `intent`: that is REPLACED wholesale, so
	 * sending `{ goal }` alone clears the domains. Send the whole object.
	 * Merging would make removing a domain impossible to express.
	 */
	update: (
		teamId: string,
		patch: Partial<{
			name: string;
			description: string | null;
			intent: OrgIntent;
		}>,
	) => http.patch<{ team: OrgTeam }>(`/api/org/teams/${teamId}`, patch),

	/** Archives — the team and its member history survive, it simply stops
	 *  being one of the choices. */
	archive: (teamId: string) =>
		http.del<{ team: OrgTeam }>(`/api/org/teams/${teamId}`),

	/** By member id or email; an admin usually has the latter. */
	addMember: (teamId: string, member: { memberId?: string; email?: string }) =>
		http.post<{ member: OrgMember }>(
			`/api/org/teams/${teamId}/members`,
			member,
		),

	removeMember: (teamId: string, memberId: string) =>
		http.del<{ member: OrgMember }>(
			`/api/org/teams/${teamId}/members/${memberId}`,
		),

	/** Org-wide intent, layered under the teams'. PUT, not PATCH — replaced
	 *  wholesale for the same reason a team's is. */
	setOrgIntent: (intent: OrgIntent) =>
		http.put<{ intent: OrgIntent }>("/api/org/intent", { intent }),

	/** The domain labels an intent may name. Global, not per-tenant, so it is
	 *  cached indefinitely by the hook. */
	domains: (signal?: AbortSignal) =>
		http.get<{ domains: string[] }>("/api/org/domains", { signal }),
};
